import type { components } from "@oathera/contract/api";
import type { Measure, StatusLevel } from "../../components/index.ts";
import type { DecisionBreakdownBy } from "./use-decisions.ts";

/**
 * Presentation vocabulary for the Decisions screen (task 11.6; design 12.2 Decisions row, 12.3
 * principles). It keeps the one-way maps from contract shapes onto the screen's presentational
 * pieces in a single, testable place:
 *
 * - how a `Quantity`'s exactness becomes an exact/estimate/unverifiable `Measure` (design 12.3);
 * - the human labels for the `by` dimensions the breakdown control offers;
 * - how a breakdown row's `highlight` becomes a prominent fail-closed affordance (icon+text, not
 *   colour alone; design 12.3 / 12.4);
 * - the drill-down `/events` query strings — both the server-provided `drill` {@link EventQuery} on a
 *   breakdown row (preferred, authoritative) and the honest standing filters the screen builds for
 *   the fail-closed totals, which the contract does not attach a `drill` to.
 *
 * It defines no API shapes; those come from `@oathera/contract/api` via `use-decisions.ts`.
 */

type Quantity = components["schemas"]["Quantity"];
type EventQuery = components["schemas"]["EventQuery"];

/** Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity): Measure {
  // No data cannot be verified: neither an exact counter nor a sampled estimate, so never green.
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/** Map the summary `series.exactness` onto a column measure for the over-time chart/table. */
export function exactnessMeasure(exactness: components["schemas"]["Exactness"]): Measure {
  return exactness === "estimated" ? "estimate" : "exact";
}

/* ---- The `by` dimension control -------------------------------------------------------------- */

/** A dimension the breakdown can be grouped by, with its control label and column header. */
export interface BreakdownDimension {
  readonly by: DecisionBreakdownBy;
  /** Control label (the toggle button text). */
  readonly label: string;
  /** The breakdown table's key-column header for this dimension. */
  readonly keyHeader: string;
}

/**
 * The four dimensions `GET /decisions/breakdown?by=…` accepts, in the order design 12.2 lists them
 * (reason, tier, operation, gateway). This is the single source for the control; it is derived from
 * the contract `DecisionBreakdownBy` enum members, so it cannot drift from the API.
 */
export const BREAKDOWN_DIMENSIONS: readonly BreakdownDimension[] = [
  { by: "reason", label: "Reason", keyHeader: "Reason" },
  { by: "tier", label: "Tier", keyHeader: "Tier" },
  { by: "operation", label: "Operation", keyHeader: "Operation" },
  { by: "gateway", label: "Gateway", keyHeader: "Gateway" },
];

/* ---- Fail-closed highlight (THE named behaviour; FR-3.3) ------------------------------------- */

/**
 * Whether a breakdown row is a fail-closed row. The contract carries this on the row's `highlight`
 * flag (`none` | `fail_closed`), so the screen reads it rather than inferring it from the key. A
 * fail-closed row is surfaced with a distinct, high-visibility affordance (icon + text), never
 * colour alone (design 12.3 "fail-closed events highlighted"; 12.4).
 */
export function isFailClosedRow(highlight: "none" | "fail_closed"): boolean {
  return highlight === "fail_closed";
}

/**
 * The two contract reason codes that ARE fail-closed decisions, from the frozen `BoundaryReason`
 * set: a decision the boundary could not reach (`decision_unavailable`) and one it could not verify
 * (`decision_unverifiable`). The summary's `totals.fail_closed` splits exactly along these two, and
 * they are real `/events` `reason` filter values — so the fail-closed panel can drill to the events
 * behind each without inventing anything.
 */
export const FAIL_CLOSED_REASONS = ["decision_unavailable", "decision_unverifiable"] as const;
export type FailClosedReason = (typeof FAIL_CLOSED_REASONS)[number];

/** The human label for a fail-closed sub-count. */
export function failClosedLabel(reason: FailClosedReason): string {
  return reason === "decision_unavailable" ? "Decision unavailable" : "Decision unverifiable";
}

/**
 * A fail-closed total is never "healthy": any non-zero fail-closed count is a warning the operator
 * must see (icon + text), and a null (no data) is grey `unknown`, never green. Zero is neutral.
 */
export function failClosedLevel(value: number | null): StatusLevel {
  if (value === null) return "unknown";
  return value > 0 ? "warn" : "neutral";
}

/* ---- Drill-down to /events ------------------------------------------------------------------- */

/**
 * The relative `/events` link for a row's server-provided `drill` filter. The breakdown row carries
 * an {@link EventQuery} — the exact query-string params of `GET /events` that show the events behind
 * that number — so the screen turns it straight into a link. The values are untrusted server data;
 * `URLSearchParams` encodes them. Linking by query keeps the drill forward-compatible with the
 * events screen (task 11.12), which reads these params.
 */
export function eventsHref(drill: EventQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(drill)) {
    if (typeof value === "string" && value.length > 0) params.set(key, value);
  }
  const qs = params.toString();
  return qs.length > 0 ? `/events?${qs}` : "/events";
}

/**
 * The honest standing `/events` filter for a fail-closed sub-count the contract does NOT attach a
 * `drill` to (the summary totals). A fail-closed decision is a `deny` with one of the two
 * {@link FAIL_CLOSED_REASONS}; both are real `/events` filter params (`decision`, `reason`), so this
 * is a faithful filter, not an invented route.
 */
export function failClosedHref(reason: FailClosedReason): string {
  const params = new URLSearchParams({ decision: "deny", reason });
  return `/events?${params.toString()}`;
}

/**
 * The standing `/events` filter for all denies in the window — the drill for the deny totals, which
 * the contract leaves without a `drill`. `decision=deny` is a real `/events` filter param.
 */
export function denyHref(): string {
  return "/events?decision=deny";
}

/** The standing `/events` filter for all decisions (allow + deny). */
export function allDecisionsHref(): string {
  return "/events";
}
